/**
 * Shared Google Maps API loader using the importLibrary bootstrap method.
 * Extracted from GMapLoader.vue to avoid duplicate loading code.
 * Safe to call multiple times — the API is only loaded once.
 */

let loadPromise = null;

export function loadGoogleMapsAPI() {
  if (loadPromise) return loadPromise;

  if (window.google && window.google.maps && window.google.maps.importLibrary) {
    loadPromise = Promise.resolve();
    return loadPromise;
  }

  loadPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.innerHTML = `
      (g => {
        var h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary", q = "__ib__", m = document, b = window;
        b = b[c] || (b[c] = {});
        var d = b.maps || (b.maps = {}), r = new Set, e = new URLSearchParams, u = () => h || (h = new Promise(async (f, n) => {
          await (a = m.createElement("script"));
          e.set("libraries", [...r] + "");
          for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]);
          e.set("callback", c + ".maps." + q);
          a.src = \`https://maps.\${c}apis.com/maps/api/js?\` + e;
          d[q] = f;
          a.onerror = () => h = n(Error(p + " could not load."));
          a.nonce = m.querySelector("script[nonce]")?.nonce || "";
          m.head.append(a);
        }));
        d[l] ? console.warn(p + " only loads once. Ignoring:", g) : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n))
      })({
        key: "${process.env.MIX_GMAPS_API_KEY}",
        v: "weekly"
      });
    `;
    document.head.appendChild(script);

    const checkGoogle = () => {
      if (window.google && window.google.maps && window.google.maps.importLibrary) {
        resolve();
      } else {
        setTimeout(checkGoogle, 100);
      }
    };
    checkGoogle();
  });

  return loadPromise;
}

/**
 * Import specific Google Maps libraries on demand.
 * Ensures the bootstrap is loaded first, then imports the requested libraries.
 * @param  {...string} libs - Library names (e.g. "maps", "places", "marker", "drawing", "geocoding")
 * @returns {Promise} Resolves when all requested libraries are loaded
 */
export async function importGoogleMapsLibraries(...libs) {
  await loadGoogleMapsAPI();
  await Promise.all(libs.map(lib => window.google.maps.importLibrary(lib)));
}
